import React from "react"
import { Helmet } from "react-helmet-async"
import { RotateCcw, Calendar, Package, CheckCircle, XCircle, Mail } from "lucide-react"

const ReturnPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Return Policy - Anmol Furniture Bazar</title>
        <meta 
          name="description" 
          content="Our return policy allows returns within 7 days of purchase. Items must be unused and in original packaging." 
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Return Policy</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding our return and exchange procedures for your purchases
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {/* Return Timeline */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Calendar className="w-6 h-6 mr-3 text-primary-600" />
                Return Timeline
              </h2>
              <div className="bg-green-50 p-6 rounded-lg border border-green-200 mb-6">
                <div className="flex items-center text-green-700 mb-2">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="font-semibold text-lg">7-Day Return Window</span>
                </div>
                <p className="text-green-600">
                  We offer refund/exchange within first 7 days from the date of your purchase
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex items-center text-red-700 mb-2">
                  <XCircle className="w-5 h-5 mr-2" />
                  <span className="font-semibold">After 7 Days</span>
                </div>
                <p className="text-red-600 text-sm">
                  If 7 days have passed since your purchase, you will not be offered a return, exchange or refund of any kind
                </p>
              </div>
            </div>

            {/* Eligibility Criteria */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Package className="w-6 h-6 mr-3 text-primary-600" />
                Return Eligibility Criteria
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">To be eligible for return or exchange:</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">✓</div>
                      <p className="text-gray-700">The purchased item should be unused and in the same condition as you received it</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">✓</div>
                      <p className="text-gray-700">The item must have original packaging</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold">✗</div>
                      <p className="text-gray-700">Items purchased on sale may not be eligible for return/exchange</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-blue-800">
                    <strong>Note:</strong> Only items that are found defective or damaged are replaced by us based on exchange requests.
                  </p>
                </div>
              </div>
            </div>

            {/* Exempt Categories */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <XCircle className="w-6 h-6 mr-3 text-red-500" />
                Products Exempt from Returns
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  You agree that there may be certain categories of products/items that are exempted from returns or refunds. 
                  Such categories of products would be clearly identified to you at the time of purchase.
                </p>
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <p className="text-amber-800">
                    <strong>Special Categories:</strong> Custom-made items, personalized furniture, and special order items 
                    may have different return policies which will be communicated at the time of purchase.
                  </p>
                </div>
              </div>
            </div>

            {/* Return Process */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <RotateCcw className="w-6 h-6 mr-3 text-primary-600" />
                Return Process
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Submit Return Request</h3>
                        <p className="text-gray-600 text-sm">Contact our customer service within 7 days of purchase</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Product Inspection</h3>
                        <p className="text-gray-600 text-sm">We inspect the returned product for eligibility</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Email Notification</h3>
                        <p className="text-gray-600 text-sm">You'll receive email confirmation of receipt and approval status</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">4</div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Processing</h3>
                        <p className="text-gray-600 text-sm">Approved returns/exchanges processed per our policies</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quality Check */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-primary-600" />
                Quality Assurance
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  For exchange/return accepted requests, once your returned product/item is received and inspected by us, 
                  we will send you an email to notify you about receipt of the returned/exchanged product.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  If the return has been approved after the quality check at our end, your request (return/exchange) 
                  will be processed in accordance with our policies.
                </p>
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <h2 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                Important Notes
              </h2>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Returns are subject to inspection and approval by our quality team</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Custom-made and personalized items may have different return policies</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Sale items may not be eligible for returns - check at time of purchase</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Original packaging and all accessories must be included</span>
                </li>
              </ul>
            </div>

            {/* Contact Section */}
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <Mail className="w-12 h-12 text-primary-600 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Need to Process a Return?</h2>
              <p className="text-gray-600 mb-6">
                Contact our customer service team to initiate your return request
              </p>
              <div className="space-y-2">
                <p className="text-lg"><span className="font-semibold">Website:</span> https://www.anmolfurniturebazar.com/</p>
                <p className="text-gray-600">Use the contact information provided on our website to reach our customer service team</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ReturnPolicy